import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Flex,
  Heading,
  Icon,
  List,
  ListItem,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import React from "react";
import {
  HiCheckCircle,
  HiClock,
  HiExclamationCircle,
  HiRefresh,
} from "react-icons/hi";

import {
  Button,
  LoadingIndicator,
  SettingsPageContainer,
} from "../../../components";
import {
  CurrentUserFragment,
  useRunWorkdayReportMutationMutation,
  useWorkdayReportStatusesQueryQuery,
  WorkdayReportName,
  WorkdayReportStatusesQueryDocument,
  WorkdayReportStatusesQueryQuery,
} from "../../graphql";

interface WorkdaySettingsProps {
  currentUser: CurrentUserFragment;
}

interface StatusIndicatorProps {
  lastSyncStatus?: string | null;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  lastSyncStatus,
}) => {
  if (lastSyncStatus === "in_progress") {
    return (
      <Badge
        colorScheme="blue"
        variant="subtle"
        display="flex"
        alignItems="center"
        gap={1}
        px={2}
        py={1}
      >
        <Spinner size="xs" />
        <Text fontSize="xs">Running</Text>
      </Badge>
    );
  }

  if (!lastSyncStatus || lastSyncStatus === "unknown") {
    return (
      <Badge
        colorScheme="gray"
        variant="subtle"
        display="flex"
        alignItems="center"
        gap={1}
        px={2}
        py={1}
      >
        <Icon as={HiClock} />
        <Text fontSize="xs">No sync yet</Text>
      </Badge>
    );
  }

  if (lastSyncStatus === "success") {
    return (
      <Badge
        colorScheme="green"
        variant="subtle"
        display="flex"
        alignItems="center"
        gap={1}
        px={2}
        py={1}
      >
        <Icon as={HiCheckCircle} />
        <Text fontSize="xs">Success</Text>
      </Badge>
    );
  }

  if (lastSyncStatus === "error") {
    return (
      <Badge
        colorScheme="red"
        variant="subtle"
        display="flex"
        alignItems="center"
        gap={1}
        px={2}
        py={1}
      >
        <Icon as={HiExclamationCircle} />
        <Text fontSize="xs">Error</Text>
      </Badge>
    );
  }

  // Fallback for other statuses
  return (
    <Badge
      colorScheme="yellow"
      variant="subtle"
      display="flex"
      alignItems="center"
      gap={1}
      px={2}
      py={1}
    >
      <Icon as={HiClock} />
      <Text fontSize="xs">{lastSyncStatus}</Text>
    </Badge>
  );
};

const getReportDisplayName = (reportName: WorkdayReportName): string => {
  switch (reportName) {
    case WorkdayReportName.CandidateConfig:
      return "Candidates";
    case WorkdayReportName.PositionConfig:
      return "Job Requisitions";
    case WorkdayReportName.QuestionnaireConfig:
      return "Questionnaires";
    case WorkdayReportName.ScheduledInterviewConfig:
      return "Scheduled Interviews";
    case WorkdayReportName.UserConfig:
      return "Users";
    case WorkdayReportName.ScorecardConfig:
      return "Scorecards";
    default:
      return reportName;
  }
};

export const WorkdaySettings: React.FC<WorkdaySettingsProps> = ({
  currentUser,
}) => {
  const toast = useToast();
  const { data, loading, error, refetch } =
    useWorkdayReportStatusesQueryQuery();
  const [runWorkdayReport, { loading: runningReports }] =
    useRunWorkdayReportMutationMutation();

  const handleRunReport = async (
    reportName: WorkdayReportName
  ): Promise<void> => {
    if (!currentUser.organization?.id) {
      toast({
        title: "Error",
        description: "Organization not found",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await runWorkdayReport({
        variables: {
          organizationId: currentUser.organization.id,
          name: reportName,
        },
        optimisticResponse: {
          runWorkdayReport: {
            __typename: "RunWorkdayReport",
            dispatched: true,
          },
        },
        update: (cache, { data }) => {
          if (data?.runWorkdayReport?.dispatched) {
            const existingData =
              cache.readQuery<WorkdayReportStatusesQueryQuery>({
                query: WorkdayReportStatusesQueryDocument,
              });

            if (existingData?.workdayReportStatuses) {
              const updatedStatuses = existingData.workdayReportStatuses.map(
                (report) =>
                  report.name === reportName
                    ? {
                        ...report,
                        lastSyncStatus: "in_progress" as const,
                        lastSyncTime: new Date().toISOString(),
                      }
                    : report
              );

              cache.writeQuery<WorkdayReportStatusesQueryQuery>({
                query: WorkdayReportStatusesQueryDocument,
                data: {
                  workdayReportStatuses: updatedStatuses,
                },
              });
            }
          }
        },
      });

      toast({
        title: "Report Started",
        description: `${getReportDisplayName(
          reportName
        )} sync started successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to start sync",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleRefreshStatus = (): void => {
    refetch();
    toast({
      title: "Status Refreshed",
      description: "Sync statuses have been updated",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  if (loading) {
    return (
      <SettingsPageContainer heading="Workday">
        <Flex alignItems="center">
          <LoadingIndicator />
        </Flex>
      </SettingsPageContainer>
    );
  }

  if (error) {
    return (
      <SettingsPageContainer heading="Workday">
        <Alert status="error">
          <AlertIcon />
          Failed to load Workday settings: {error.message}
        </Alert>
      </SettingsPageContainer>
    );
  }

  const reportStatuses = data?.workdayReportStatuses || [];

  return (
    <SettingsPageContainer heading="Workday">
      <Box>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Heading size="md">Configured Report Syncs</Heading>
          <Button size="sm" variant="outline" onClick={handleRefreshStatus}>
            Refresh Status
          </Button>
        </Flex>

        {reportStatuses.length === 0 ? (
          <Alert status="info">
            <AlertIcon />
            No configured Workday reports found. Reports need to be configured
            with JSON settings and enabled to appear here.
          </Alert>
        ) : (
          <List spacing={4}>
            {reportStatuses.map((report) => (
              <ListItem key={report.name}>
                <Box p={4} borderWidth={1} borderRadius="md" bg="gray.50">
                  <Flex justifyContent="space-between" alignItems="center">
                    <Box flex="1">
                      <Flex alignItems="center" gap={3} mb={2}>
                        <Text fontWeight="semibold" fontSize="lg">
                          {getReportDisplayName(report.name)}
                        </Text>
                        <StatusIndicator
                          lastSyncStatus={report.lastSyncStatus}
                        />
                      </Flex>

                      <Flex alignItems="center" gap={2}>
                        {report.lastSyncStatus === "in_progress" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<Icon as={HiRefresh} />}
                            onClick={handleRefreshStatus}
                          >
                            Refresh Status
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              isLoading={runningReports}
                              isDisabled={
                                report.lastSyncStatus === "in_progress"
                              }
                              onClick={() => handleRunReport(report.name)}
                            >
                              Run Now
                            </Button>
                            {report.lastSyncTime && (
                              <Text fontSize="sm" color="gray.600">
                                {formatDistanceToNow(
                                  new Date(report.lastSyncTime),
                                  { addSuffix: true }
                                )}
                              </Text>
                            )}
                          </>
                        )}
                      </Flex>
                    </Box>
                  </Flex>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </SettingsPageContainer>
  );
};
